import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { FormControlLabel } from '#src/atoms/inputs/FormControlLabel/index.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL, LabelM } from '#src/atoms/typography/index.tsx';
import { StepWrapper } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import { LEARN_MORE_ABOUT_FIAT_WALLET } from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { CreateFiatWalletSteps } from '#src/components/fiatWallet/CreateFiatWallet/type.ts';
import { useCurrencyOptions } from '#src/components/fiatWallet/CreateFiatWallet/useCurrencyOptions.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm } from '#src/components/form/Form/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { glossary } from '#src/lib/glossary.ts';

const Content = styled(Vertical).attrs({ gap: 3 })`
  justify-content: flex-start;
  height: 100%;
`;

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
  display: flex;
  flex-grow: 1;
`;

const StyledLink = styled.a`
  font-weight: bold;
  text-decoration: underline;
  color: var(--c-nd-600);
`;

const Block = styled(Horizontal).attrs({ gap: 2 })`
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-nd-100);
`;

const Asterisk = styled.span`
  color: var(--c-red-400);
`;

type Props = {
  presetCurrency?: FiatCurrency;
  onChange: (currency: FiatCurrency) => void;
  onSubmit: () => Promise<GraphQLResult>;
  onSuccess?: () => void;
  setStep: (step: CreateFiatWalletSteps) => void;
};

export const ChooseCurrency = ({
  presetCurrency,
  onSubmit: doOnSubmit,
  onChange,
  setStep,
  onSuccess,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const [currency, setCurrency] = useState<FiatCurrency | undefined>(
    presetCurrency
  );
  const currencies = useCurrencyOptions();

  const onSubmit = async (
    values: any,
    onResult: (result: GraphQLResult) => void
  ) => {
    const res = await doOnSubmit();
    onResult(res);
  };

  const disabled = !currency;

  return (
    <StyledGraphqlForm
      onSubmit={(variables, onResult) => {
        onSubmit(variables, onResult);
      }}
      onSuccess={() => {
        if (onSuccess) onSuccess();
        setStep(CreateFiatWalletSteps.REVIEW_INFORMATION);
      }}
      render={(Error, SubmitButton) => (
        <StepWrapper
          setStep={setStep}
          step={CreateFiatWalletSteps.CHOOSE_CURRENCY}
          submitButton={
            <SubmitButton size="medium" disabled={disabled}>
              <FormattedMessage {...glossary.next} />
            </SubmitButton>
          }
        >
          <Content>
            <>
              <HeadlineL as="h3">
                <FormattedMessage defaultMessage="Choose your currency" />
              </HeadlineL>
              <BodyM color="var(--c-nd-600)">
                <FormattedMessage
                  defaultMessage="Choose the currency you prefer for your Cash Wallet. Card prices will also be shown in the currency you select. <link>Learn more</link>"
                  values={{
                    link: (...children) => (
                      <StyledLink
                        href={LEARN_MORE_ABOUT_FIAT_WALLET}
                        target="_blank"
                      >
                        {children}
                      </StyledLink>
                    ),
                  }}
                />
              </BodyM>
              <Block>
                <FontAwesomeIcon
                  icon={iconInfoCircle}
                  color="var(--c-nd-600)"
                />
                <LabelM color="var(--c-nd-700)" bold>
                  <FormattedMessage defaultMessage="Once selected, you cannot change it." />
                </LabelM>
              </Block>
              <FormControlLabel
                labelPlacement="top"
                control={
                  <NativeSelect
                    fullWidth
                    placeholder={formatMessage({
                      defaultMessage: 'Choose your currency',
                    })}
                    name="currency"
                    values={currencies}
                    value={currency ?? ''}
                    onChange={value => {
                      setCurrency(value as FiatCurrency);
                      onChange(value as FiatCurrency);
                    }}
                  />
                }
                label={
                  <BodyM bold color="var(--c-white)">
                    <FormattedMessage defaultMessage="Currency" />{' '}
                    <Asterisk>*</Asterisk>
                  </BodyM>
                }
              />
              <Error />
            </>
          </Content>
        </StepWrapper>
      )}
    />
  );
};
